import moment from "moment";
import React, { useState, forwardRef } from "react";
import "./PrescriptionPDF.scss";

const displayName = (item) => {
  if (item == null) return "";
  if (typeof item === "string") return item;
  return (
    item?.name ||
    item?.testId?.name ||
    item?.drugId?.drugName ||
    ""
  );
};

const hasValue = (value) => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  return String(value).trim() !== "";
};

const DURATION_UNIT_LABELS = {
  days: "day(s)",
  weeks: "week(s)",
  months: "month(s)",
};

/**
 * Render a prescribed duration for display, e.g. "1 day(s)", "5 day(s)",
 * "2 month(s)". Legacy drugs stored a bare number that always meant days, so a
 * missing unit falls back to days.
 *
 * Mirrors the doctor portal's drugTableColumns.js. Reimplemented locally on
 * purpose: the apps are separate repos with no shared package.
 */
export const formatDuration = (duration, durationUnit) => {
  const amount = Number(duration);
  if (!Number.isFinite(amount) || amount <= 0) {
    return "";
  }

  const label = DURATION_UNIT_LABELS[durationUnit] || DURATION_UNIT_LABELS.days;

  return `${amount} ${label}`;
};

// Drug name plus its generic name in parentheses, exactly as the doctor portal prints it.
const formatDrugName = (drug) => {
  const name = drug?.drugId?.drugName;
  if (!name) return "";
  const genericName = drug?.drugId?.genericName;
  return genericName ? `${name} (${genericName})` : name;
};

// Column definitions mirror drcfo.frontend's PrescriptionPDF/drugTableColumns.js so the
// patient's PDF matches the doctor's. These read the real prescription.drugs[] shape:
// drugId/doses/dosageTime are populated refs, not plain strings.
const DRUG_COLUMNS = [
  { key: "sno", label: "S.No", always: true },
  { key: "name", label: "Name", always: true, value: formatDrugName },
  {
    key: "drugType",
    label: "Drug Type",
    value: (drug) => drug?.drugId?.drugType?.name,
  },
  { key: "dose", label: "Dose", value: (drug) => drug?.unit },
  { key: "frequency", label: "Frequency", value: (drug) => drug?.doses?.name },
  {
    key: "timing",
    label: "Timing",
    value: (drug) =>
      (Array.isArray(drug?.dosageTime) ? drug.dosageTime : [])
        .map((item) => item?.name)
        .filter(Boolean)
        .join(", "),
  },
  {
    key: "duration",
    label: "Duration",
    value: (drug) => formatDuration(drug?.duration, drug?.durationUnit),
  },
  {
    key: "instruction",
    label: "Instruction",
    value: (drug) => drug?.description,
  },
];

// Only render columns that at least one drug actually has data for, so empty
// columns don't print as blank strips.
export const getVisibleDrugColumns = (drugs) => {
  const rows = Array.isArray(drugs) ? drugs : [];

  return DRUG_COLUMNS.filter(
    (column) =>
      column.always || rows.some((drug) => hasValue(column.value(drug))),
  );
};

const GENDER_LABELS = {
  MALE: "Male",
  FEMALE: "Female",
};

// Mirrors the doctor portal: only MALE/FEMALE are spelled out, anything else is N/A.
const formatGender = (gender) => GENDER_LABELS[gender] || "N/A";

const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const dob = moment(dateOfBirth);
  return dob.isValid() ? moment().diff(dob, "years") : null;
};

const formatAddress = (address) =>
  [
    address?.address1,
    address?.address2,
    address?.cityVillage,
    address?.taluka,
    address?.district,
    address?.state,
    address?.pincode,
  ]
    .filter(Boolean)
    .join(", ");

const PrescriptionPDF = forwardRef((props, ref) => {
  const [appointmentData] = useState(props.appointment);

  // `prescription` is an ObjectId ref on the appointment. GET /api/appointment/patient
  // now populates it (and its drug refs), but older/cached responses can still hand back
  // a bare ObjectId/string, so guard instead of treating it as a prescription document.
  const prescription =
    appointmentData?.prescription &&
    typeof appointmentData.prescription === "object"
      ? appointmentData.prescription
      : {};
  const complaints = prescription?.complain || [];
  const diagnosis = prescription?.diagnosis || [];
  const treatments = prescription?.treatment || [];
  // The schema field is `tests` (rows of { testId, remarks }); `test` never
  // existed, so prescribed tests were silently absent from the patient PDF.
  const tests = prescription?.tests || prescription?.test || [];
  const drugs = prescription?.drugs || [];
  const visibleDrugColumns = getVisibleDrugColumns(drugs);

  const patient = appointmentData?.patient;
  const unregisteredPatient = appointmentData?.unregisteredPatient;

  const patientName = patient?.name ?? unregisteredPatient?.name;
  const patientPhone = patient?.phone ?? unregisteredPatient?.phone;
  const patientGender = patient?.gender ?? unregisteredPatient?.gender;
  const patientAge =
    calculateAge(patient?.dateOfBirth) ??
    patient?.age ??
    calculateAge(unregisteredPatient?.dateOfBirth) ??
    unregisteredPatient?.age;

  const patientAddress = formatAddress(appointmentData?.address);

  // ABHA lives on the appointment document (healthID / abhaAddress); show only when both exist.
  const showABHA =
    appointmentData?.healthID && appointmentData?.abhaAddress ? true : false;

  const clinicAddress = [
    appointmentData?.clinic?.address?.street,
    appointmentData?.clinic?.address?.area,
    appointmentData?.clinic?.address?.city,
    appointmentData?.clinic?.address?.state,
    appointmentData?.clinic?.address?.pincode,
  ]
    .filter(Boolean)
    .join(", ");

  const appointmentDateTime = appointmentData?.createdAt
    ? moment
        .utc(appointmentData.createdAt)
        .utcOffset("+05:30")
        .format("DD / MM / YYYY, hh:mm A")
    : null;

  const followUpDate = prescription?.followUp?.followUpDate;

  const validity = prescription?.validity;
  const expiryDate =
    appointmentData?.date && (validity || validity === 0)
      ? moment(appointmentData.date).add(validity, "days").format("DD/MM/YYYY")
      : null;

  return (
    <div ref={ref}>
      <div className="PrescriptionPDF_box">
        <div className="prescription_clinic_box">
          <div className="prescription_clinic_inner_box">
            <div className="">
              {appointmentData?.clinic?.name && (
                <h3>{appointmentData.clinic.name}</h3>
              )}
              {appointmentData?.doctor?.name && (
                <h4>Dr. {appointmentData.doctor.name}</h4>
              )}
            </div>
            <div className="">
              {appointmentData?.doctor?.phone && (
                <p>Contact: {appointmentData.doctor.phone}</p>
              )}
              {appointmentData?.doctor?.email && (
                <p>Email: {appointmentData.doctor.email}</p>
              )}
              {clinicAddress && <h6>Address: {clinicAddress}</h6>}
            </div>
          </div>
        </div>

        <div className="prescription_patient_container">
          <div className="prescription_patient_box">
            <h2>Patient Information</h2>

            <div className="prescription_patient_info_grid">
              <div className="prescription_patient_info_row">
                <span className="label">Patient Name</span>
                <span className="value">{patientName || "N/A"}</span>
              </div>

              {appointmentData?.appointmentPatientId && (
                <div className="prescription_patient_info_row">
                  <span className="label">Unique ID</span>
                  <span className="value">
                    {appointmentData.appointmentPatientId}
                  </span>
                </div>
              )}

              {appointmentDateTime && (
                <div className="prescription_patient_info_row">
                  <span className="label">Date &amp; Time</span>
                  <span className="value">{appointmentDateTime}</span>
                </div>
              )}

              <div className="prescription_patient_info_row">
                <span className="label">Age / Gender</span>
                <span className="value">
                  {`${patientAge ?? "N/A"} (${formatGender(patientGender)})`}
                </span>
              </div>

              {patientPhone && (
                <div className="prescription_patient_info_row">
                  <span className="label">Phone Number</span>
                  <span className="value">{patientPhone}</span>
                </div>
              )}

              {patientAddress && (
                <div className="prescription_patient_info_row">
                  <span className="label">Address</span>
                  <span className="value">{patientAddress}</span>
                </div>
              )}

              {showABHA && (
                <div className="prescription_patient_info_row">
                  <span className="label">ABHA Number</span>
                  <span className="value">{appointmentData.healthID}</span>
                </div>
              )}

              {showABHA && (
                <div className="prescription_patient_info_row">
                  <span className="label">ABHA Address</span>
                  <span className="value">{appointmentData.abhaAddress}</span>
                </div>
              )}

              {appointmentData?.panelRegistrationNo && (
                <div className="prescription_patient_info_row">
                  <span className="label">Beneficiary ID</span>
                  <span className="value">
                    {appointmentData.panelRegistrationNo}
                  </span>
                </div>
              )}

              {appointmentData?.referringDoctorName && (
                <div className="prescription_patient_info_row">
                  <span className="label">Referring Doctor</span>
                  <span className="value">
                    {appointmentData.referringDoctorName}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="patient_prescription_content">
          <div className="prescription_item">
            <h1>Complain</h1>
            <ul>
              {complaints?.length
                ? complaints.map((item, id) => <li key={id}>{displayName(item)}</li>)
                : "No Complains Found"}
            </ul>
          </div>
          <div className="prescription_item">
            <h1>Diagnosis</h1>
            <ul>
              {diagnosis?.length
                ? diagnosis.map((item, id) => <li key={id}>{displayName(item)}</li>)
                : "No Diagnosis Found"}
            </ul>
          </div>
          <div className="prescription_item">
            <h1>Treatment</h1>
            <ul>
              {treatments?.length
                ? treatments.map((item, id) => <li key={id}>{displayName(item)}</li>)
                : "No Treatments Found"}
            </ul>
          </div>
        </div>

        <div className="prescription_test_container">
          <div className="prescription_test_item">
            <h1>Test</h1>
            <ol>
              {tests?.length ? (
                tests.map((test, index) => <li key={index}>{displayName(test)}</li>)
              ) : (
                <p>-</p>
              )}
            </ol>
          </div>
        </div>

        <div className="prescription_drugs_table">
          <table>
            <thead>
              <tr>
                {visibleDrugColumns.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {drugs?.length ? (
                drugs.map((drug, index) => {
                  return (
                    <tr key={index}>
                      {visibleDrugColumns.map((column) => {
                        if (column.key === "sno") {
                          return <td key={column.key}>{index + 1}</td>;
                        }

                        const value = column.value(drug);

                        return (
                          <td key={column.key}>
                            {hasValue(value) ? value : "-"}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={visibleDrugColumns.length}
                    style={{ textAlign: "center" }}
                  >
                    No medicines added
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {followUpDate && (
          <div className="prescription_follow_update">
            <div className="prescription_follow_update_wrapper">
              <h1>Follow Up Date</h1>
              <p>{moment(followUpDate).format("DD / MM / YYYY")}</p>
            </div>
          </div>
        )}

        {expiryDate && (
          <div className="prescription_validity_box">
            <h1>Valid till : {expiryDate}</h1>
            <p>
              This prescription is not valid for medico-legal purposes. The
              validity of this prescription is only up to {validity} days.
            </p>
          </div>
        )}

        {appointmentData?.doctor?.name && (
          <div className="prescription_signature_box">
            <label>Dr. {appointmentData.doctor.name}</label>
            {appointmentData?.date && (
              <label>
                {moment(appointmentData.date).format("DD/MM/YYYY")}
              </label>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default PrescriptionPDF;
